/**
 * Adapts the flat `PublicRosterRow[]` a public share-view payload carries
 * into the shape ContentTab's columns read.
 *
 * A SIBLING of Scripts' (`../ScriptsTab.publicRosterRowAdapter.ts`) — same
 * BE builder path (`buildProductionRosterView`), same flat wire shape, same
 * reasoning. `RosterEntryContent` mirrors `RosterEntryScript` field-for-field
 * plus one extra field, `live_url`; there is no `snapshot_metrics` or
 * `deliverables` field at all on this tab's target type, so those two shared
 * helpers don't apply here (same as Scripts).
 *
 * **Why `status` is a frozen, never-real placeholder (same reasoning as
 * Scripts' adapter):** the BE's `share_view_page.go` builds every roster
 * tab's public row via the SAME `buildPublicRosterRow(entry)`, so
 * `PublicRosterRow.status` is ALWAYS the roster ENTRY's negotiation status
 * (e.g. `accepted_by_creator`), never the production POST's review status
 * (`content_pending` / `content_review`) — there is no separate field on the
 * wire for that at all. `buildProductionRosterView` (the builder Content
 * routes through, same as Scripts) only changes WHICH entries appear on a
 * granted page (filtered by roster_posts status membership); it does not add
 * a per-row content-status field. Freezing to a *specific* value here would
 * be actively misleading if ever read for real rendering — the placeholder
 * exists purely to satisfy `RosterEntryContent.status`'s type structurally.
 *
 * **Why `live_url` is DIFFERENT from `status`/`last_message` and passes
 * straight through:** unlike `status` (wrong semantics if passed through) or
 * `last_message`/`unread_count`/thread-locked flags (no wire field at all),
 * `live_url` genuinely IS the same field, with the same meaning, on both the
 * wire (`PublicRosterRow.live_url`) and the target type
 * (`RosterEntryContent.live_url`) — `share_view_page.go`'s
 * `buildPublicRosterRow` sets it from the same content-version URL the authed
 * endpoint serves. It is documented (Task 16/18's reviews) as ALWAYS EMPTY on
 * the wire TODAY — a known BE gap, not a mapping problem — so passing it
 * through as `row.live_url` is correct now (naturally always `undefined`) and
 * stays correct the day the BE gap closes, with no adapter change needed.
 * Nothing reads it for real in public mode regardless: ContentTab.tsx's
 * Action column (the only place `live_url` drives anything — the Add/
 * Re-submit URL button label) is forced to a permanent skeleton off
 * `isPublicView` for every row, never off this value.
 *
 * Consequently `ContentTab.tsx` must force the Status / Last comment /
 * Updated / Action cells to a permanent skeleton off the component-level
 * `isPublicView` flag for EVERY row, never off this adapter's placeholder
 * values (`status`, `status_updated_at`, `last_message`, `unread_count`,
 * `script_thread_locked`, `content_thread_locked`) — none of those fields
 * has a wire representation, ever, at any point on the BE's builder path
 * today. Only the Creator column (platform identity) reads real data.
 */
import type { PublicRosterRow } from '@discovr/features';
import type { RosterEntryContent } from '@discovr/types';
import { adaptPublicRosterRowIdentity } from '../publicRosterRowSharedFields';

/** Adapted row shape, structurally close to `RosterEntryContent` but with
 *  every production-phase field forced to an inert placeholder — see this
 *  file's header for why none of them (besides `platform` and `live_url`)
 *  can ever be anything but a placeholder here. */
export interface PublicContentRow {
  id: string;
  user_id: string;
  status: 'content_pending' | 'content_review';
  status_updated_at: string;
  platform: {
    username: string;
    name: string;
    profile_picture_url: string;
    is_verified: boolean;
  };
  live_url?: string;
  last_message?: undefined;
  unread_count: number;
  script_thread_locked: boolean;
  content_thread_locked: boolean;
  timestamps: { created_at: string; updated_at: string };
}

function adaptPublicRosterRow(row: PublicRosterRow): PublicContentRow {
  return {
    ...adaptPublicRosterRowIdentity(row),
    // Frozen placeholder — see this file's header. Never read for real
    // rendering: ContentTab.tsx's Status/Action cells force a skeleton off
    // `isPublicView`, not off this value.
    status: 'content_pending',
    status_updated_at: '',
    // Genuinely the same field, passed straight through — see this file's
    // header for why this one (unlike status/last_message) isn't frozen.
    live_url: row.live_url,
    last_message: undefined,
    unread_count: 0,
    script_thread_locked: true,
    content_thread_locked: true,
    timestamps: { created_at: '', updated_at: '' },
  };
}

export function adaptPublicRosterRows(rows: PublicRosterRow[]): PublicContentRow[] {
  return rows.map(adaptPublicRosterRow);
}

/** Cast helper for the one call site (ContentTab.tsx) that must feed these
 *  rows into `DataTable<RosterEntryContent>`. Kept as a named export (rather
 *  than an inline `as unknown as` at the call site) so the type gap is
 *  documented in exactly one place — mirrors
 *  `adaptPublicRosterRowsAsScriptRows` / `adaptPublicRosterRowsAsFinalizedRows`
 *  in the sibling adapters. */
export function adaptPublicRosterRowsAsContentRows(rows: PublicRosterRow[]): RosterEntryContent[] {
  return adaptPublicRosterRows(rows) as unknown as RosterEntryContent[];
}
